import React, { useEffect, useRef, useState } from 'react';
import { KBBlockType } from '../../../types';

interface BlockOption {
  type: KBBlockType;
  label: string;
  description: string;
  icon: string;
  group: string;
}

const BLOCK_OPTIONS: BlockOption[] = [
  // Text
  { type: 'heading_1', label: 'Heading 1', description: 'Large section heading', icon: 'H1', group: 'Text' },
  { type: 'heading_2', label: 'Heading 2', description: 'Medium section heading', icon: 'H2', group: 'Text' },
  { type: 'heading_3', label: 'Heading 3', description: 'Small section heading', icon: 'H3', group: 'Text' },
  { type: 'paragraph', label: 'Paragraph', description: 'Plain text with inline formatting', icon: '¶', group: 'Text' },
  // Lists
  { type: 'bullet_list', label: 'Bullet list', description: 'Unordered list of items', icon: '•', group: 'Lists' },
  { type: 'ordered_list', label: 'Numbered list', description: 'Ordered list of items', icon: '1.', group: 'Lists' },
  { type: 'checklist', label: 'Checklist', description: 'Interactive checkboxes', icon: '☑', group: 'Lists' },
  { type: 'toggle', label: 'Toggle', description: 'Collapsible section', icon: '▶', group: 'Lists' },
  // Media
  { type: 'image', label: 'Image', description: 'Upload or embed an image', icon: '🖼', group: 'Media' },
  { type: 'video', label: 'Video', description: 'YouTube or Vimeo embed', icon: '▶️', group: 'Media' },
  { type: 'file', label: 'File', description: 'Attach a file', icon: '📎', group: 'Media' },
  // Content
  { type: 'code', label: 'Code', description: 'Code block with syntax highlighting', icon: '</>', group: 'Content' },
  { type: 'table', label: 'Table', description: 'Simple data table', icon: '⊞', group: 'Content' },
  { type: 'callout', label: 'Callout', description: 'Info, warning, or success callout', icon: '💡', group: 'Content' },
  { type: 'bookmark', label: 'Bookmark', description: 'Link preview card', icon: '🔖', group: 'Content' },
  // Layout & structure
  { type: 'table_of_contents', label: 'Table of contents', description: 'Auto-generated from headings', icon: '≡', group: 'Structure' },
  { type: 'divider', label: 'Divider', description: 'Horizontal rule', icon: '—', group: 'Structure' },
  { type: 'spacer', label: 'Spacer', description: 'Empty vertical space', icon: '↕', group: 'Structure' },
  // Widgets
  { type: 'progress', label: 'Progress bar', description: 'Visual progress indicator', icon: '█', group: 'Widgets' },
  { type: 'status_badge', label: 'Status badge', description: 'Inline status indicator', icon: '🟢', group: 'Widgets' },
  { type: 'markdown', label: 'Markdown', description: 'Write raw markdown, preview rendered', icon: 'MD', group: 'Content' },
];

const GROUPS = ['Text', 'Lists', 'Media', 'Content', 'Structure', 'Widgets'];

interface Props {
  onSelect: (type: KBBlockType) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement>;
}

const BlockMenu: React.FC<Props> = ({ onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filtered = search.trim()
    ? BLOCK_OPTIONS.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          o.description.toLowerCase().includes(search.toLowerCase())
      )
    : BLOCK_OPTIONS;

  const grouped = GROUPS.map((g) => ({
    group: g,
    items: filtered.filter((o) => o.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      ref={menuRef}
      className="w-72 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50"
      role="menu"
    >
      <div className="px-3 pt-3 pb-2 border-b border-gray-100">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search blocks…"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && filtered.length > 0) {
              onSelect(filtered[0].type);
              onClose();
            }
          }}
          className="w-full text-sm outline-none placeholder-gray-300 text-gray-700"
        />
      </div>
      <div className="overflow-y-auto max-h-80 py-1">
        {grouped.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No blocks found</p>
        )}
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-2">{group}</p>
            {items.map((opt) => (
              <button
                key={opt.type}
                onClick={() => { onSelect(opt.type); onClose(); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                role="menuitem"
              >
                <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-sm font-mono text-gray-600 flex-shrink-0">
                  {opt.icon}
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-700">{opt.label}</p>
                  <p className="text-xs text-gray-400">{opt.description}</p>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlockMenu;
